import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { CheckCircle, Circle, AlertCircle } from 'lucide-react';

const TAREAS_ONBOARDING = [
  { id: 'fianza_recibida', label: 'Fianza recibida', adminOnly: true },
  { id: 'gustos_musicales', label: 'Gustos musicales recopilados' },
  { id: 'canciones_especiales', label: 'Canciones imperdibles/prohibidas' },
  { id: 'horarios_confirmados', label: 'Horarios confirmados' },
  { id: 'montaje_elegido', label: 'Montaje elegido' },
  { id: 'logo_fotomaton', label: 'Logo fotomatón recibido', conditional: 'Fotomatón' },
  { id: 'musica_360', label: 'Música 360 elegida', conditional: '360' },
  { id: 'contrato_firmado', label: 'Contrato firmado' },
];

export const ChecklistOnboarding = ({ playlist, onUpdate, isAdmin, API }) => {
  const [loading, setLoading] = useState(false);

  const handleToggle = async (tareaId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/playlists/${playlist.id}/checklist/toggle?tarea=${tareaId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Error al actualizar');

      const data = await response.json();
      onUpdate(data.checklist);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if a task is completed
  const isCompleted = (tareaId) => {
    return playlist.checklist_onboarding?.some(item => item.tarea === tareaId && item.completada) || false;
  };

  // Filter tasks based on services and admin access
  const filteredTareas = TAREAS_ONBOARDING.filter(tarea => {
    // Hide admin-only tasks for non-admins
    if (tarea.adminOnly && !isAdmin) return false;
    
    // Hide conditional tasks if service not contracted
    if (tarea.conditional) {
      const hasService = playlist.servicios_contratados?.some(
        s => s.servicio === tarea.conditional && s.contratado
      );
      if (!hasService) return false;
    }
    
    return true;
  });

  // Calculate progress
  const completadas = filteredTareas.filter(t => isCompleted(t.id)).length;
  const total = filteredTareas.length;
  const progreso = total > 0 ? Math.round((completadas / total) * 100) : 0;

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg text-white flex items-center justify-between">
          <span>📋 Checklist de Onboarding</span>
          <span className={`text-sm px-3 py-1 rounded ${
            progreso === 100 ? 'bg-green-600 text-white' : 
            progreso >= 50 ? 'bg-yellow-600 text-white' : 
            'bg-red-600 text-white'
          }`}>
            {progreso}% Completo
          </span>
        </CardTitle>
        
        {/* Progress bar */}
        <div className="w-full bg-slate-700 rounded-full h-3 mt-3">
          <div 
            className={`h-3 rounded-full transition-all duration-300 ${
              progreso === 100 ? 'bg-green-500' : 
              progreso >= 50 ? 'bg-yellow-500' : 
              'bg-red-500'
            }`}
            style={{ width: `${progreso}%` }}
          />
        </div>
        <p className="text-xs text-slate-400 mt-1">
          {completadas} de {total} tareas completadas
        </p>
      </CardHeader>
      
      <CardContent>
        {filteredTareas.length === 0 ? (
          <p className="text-slate-400 text-sm text-center py-4">
            No hay tareas de onboarding configuradas
          </p>
        ) : (
          <div className="space-y-2">
            {filteredTareas.map(tarea => {
              const completada = isCompleted(tarea.id);
              
              return (
                <button
                  key={tarea.id}
                  onClick={() => handleToggle(tarea.id)}
                  disabled={loading}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                    completada 
                      ? 'bg-green-600/20 border border-green-600/50 hover:bg-green-600/30' 
                      : 'bg-slate-900 border border-slate-700 hover:bg-slate-700'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {completada ? (
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                  
                  <span className={`text-sm flex-1 text-left ${
                    completada ? 'text-green-200 line-through' : 'text-white'
                  }`}>
                    {tarea.label}
                    {tarea.adminOnly && (
                      <span className="ml-2 text-xs bg-amber-600 px-2 py-0.5 rounded">ADMIN</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        
        {progreso < 100 && progreso > 0 && (
          <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-600/30 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-yellow-200">
              <strong>Pendiente:</strong> Completa todas las tareas antes del evento para asegurar una experiencia perfecta.
            </p>
          </div>
        )}
        
        {progreso === 100 && (
          <div className="mt-4 p-3 bg-green-900/20 border border-green-600/30 rounded-lg flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-green-200">
              <strong>¡Perfecto!</strong> Todas las tareas de onboarding están completadas. El evento está listo.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
